/**
 * ShopSampleImagesCard — super-admin uploader for the 8-image fallback
 * pool used by /shop when a product has no explicit `image_url`.
 *
 * Sellers upload photos that match their actual catalogue so the
 * browse grid stops looking like generic Picsum stock. Any slot left
 * empty falls back to the shipped default photo, so the pool always
 * has 8 usable images.
 *
 * Backend contract:
 *   GET  /api/admin/shop/sample-images         — full pool
 *   PUT  /api/admin/shop/sample-images/{slot}  — { data_url } (or null to clear)
 *   POST /api/admin/shop/sample-images/reset   — clear every slot
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, ImageIcon, RotateCcw, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { refreshSampleImagePool, SAMPLE_IMAGE_COUNT, sampleImageFor } from "@/lib/sampleImages";
import { ShopSlotPreviewModal } from "@/pages/admin/ShopSlotPreviewModal";

type SlotMap = Record<string, string | null>;

const MAX_BYTES = 6 * 1024 * 1024;

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export const ShopSampleImagesCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const pool = useQuery<SlotMap>({
    queryKey: ["admin-shop-sample-images"],
    queryFn: async () => (await api.get("/admin/shop/sample-images")).data,
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/shop/sample-images/reset")).data,
    onSuccess: () => {
      toast.success("Restored shipped defaults for every slot.");
      qc.invalidateQueries({ queryKey: ["admin-shop-sample-images"] });
      refreshSampleImagePool();
    },
    onError: () => toast.error("Reset failed."),
  });

  return (
    <div className="sg-card p-6" data-testid="shop-sample-images-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0">
          <ImageIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">
            Shop sample photos
          </div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Upload up to {SAMPLE_IMAGE_COUNT} photos that match your catalogue.
            Every product without a real photo pulls one deterministically from
            this pool. Empty slots fall back to shipped defaults, so the pool
            always has {SAMPLE_IMAGE_COUNT} usable images.
          </div>
        </div>
        <Button
          onClick={() => reset.mutate()}
          disabled={readonly || reset.isPending}
          variant="outline"
          size="sm"
          data-testid="shop-sample-images-reset"
        >
          {reset.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
          Reset all
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: SAMPLE_IMAGE_COUNT }, (_, i) => {
          const key = `slot_${i}`;
          return (
            <SlotUploader
              key={key}
              slotKey={key}
              index={i}
              value={pool.data?.[key] || null}
              readonly={readonly}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ["admin-shop-sample-images"] });
                refreshSampleImagePool();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const SlotUploader: React.FC<{
  slotKey: string;
  index: number;
  value: string | null;
  readonly: boolean;
  onSaved: () => void;
}> = ({ slotKey, index, value, readonly, onSaved }) => {
  const [busy, setBusy] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: async (data_url: string | null) =>
      (await api.put(`/admin/shop/sample-images/${slotKey}`, { data_url })).data,
    onSuccess: () => {
      onSaved();
      toast.success(data_url_action_success(!!value));
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const handleFile = async (file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error(`Image is larger than ${MAX_BYTES / (1024 * 1024)} MB.`);
      return;
    }
    setBusy(true);
    try {
      const url = await fileToDataUrl(file);
      await save.mutateAsync(url);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className="group relative aspect-square rounded-xl overflow-hidden border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 hover:border-orange-400 transition-colors"
      data-testid={`shop-sample-slot-${index}`}
    >
      {value ? (
        <img
          src={value}
          alt={`Sample photo ${index + 1}`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid={`shop-sample-slot-${index}-image`}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 text-xs">
          <ImageIcon className="h-5 w-5 mb-1 opacity-60" />
          Slot {index + 1}
          <span className="text-[10px] mt-0.5 opacity-70">Uses default</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
        className="hidden"
        data-testid={`shop-sample-slot-${index}-input`}
        disabled={readonly || busy}
      />
      {/* Top-right corner: slot number */}
      <span className="absolute top-1.5 left-1.5 z-10 bg-slate-900/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
        {index + 1}
      </span>
      {/* Bottom controls — always visible on mobile, hover on desktop */}
      <div className="absolute bottom-0 inset-x-0 flex items-center justify-between p-1.5 gap-1 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={readonly || busy || save.isPending}
          className="flex-1 inline-flex items-center justify-center gap-1 h-7 px-2 rounded-md bg-white/95 hover:bg-white text-slate-900 text-[11px] font-semibold disabled:opacity-50"
          data-testid={`shop-sample-slot-${index}-upload`}
        >
          {busy || save.isPending
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <><Upload className="h-3 w-3" /> {value ? "Replace" : "Upload"}</>}
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-white/95 hover:bg-white text-slate-700 disabled:opacity-50"
          aria-label={`Preview slot ${index + 1} on /shop`}
          title={`Preview slot ${index + 1} on /shop`}
          data-testid={`shop-sample-slot-${index}-preview`}
        >
          <Eye className="h-3 w-3" />
        </button>
        {value && (
          <button
            type="button"
            onClick={() => save.mutate(null)}
            disabled={readonly || save.isPending}
            className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-white/95 hover:bg-white text-rose-600 disabled:opacity-50"
            aria-label={`Clear slot ${index + 1}`}
            data-testid={`shop-sample-slot-${index}-clear`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      <ShopSlotPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        slotIndex={index}
        imageUrl={value}
        fallbackUrl={sampleImageFor(`preview-fallback-${index}`, 600)}
      />
    </div>
  );
};

function data_url_action_success(had_value: boolean): string {
  return had_value ? "Sample photo replaced." : "Sample photo uploaded.";
}

export default ShopSampleImagesCard;
